import { useTranslation } from "@redotech/redo-i18n";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import PercentIcon from "@redotech/redo-icons/percent-01_filled.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { BgColorValue } from "@redotech/redo-web/theme/box";
import { TextColorValue } from "@redotech/redo-web/theme/typography";
import classNames from "classnames";
import { memo } from "react";
import * as css from "./split-variant-controls.module.css";

const MAX_VARIANTS = 6;

interface Variant {
  id: string;
  name: string;
  weight: number;
}

export interface SplitVariantControlsProps {
  variants: Variant[];
  onVariantNameChange: (id: string, name: string) => void;
  onVariantWeightChange: (id: string, weightStr: string) => void;
  onAddVariant: () => void;
  onRemoveVariant: (id: string) => void;
  onBalanceWeights: () => void;
  readOnly?: boolean;
  totalWeight: number;
  isWeightValid:
    | { isValid: true }
    | {
        isValid: false;
        errorMessage: string;
        errorLevel?: "warning" | "error";
      };
}

const isValidWeightErrorLevelToBgColor: Record<
  "warning" | "error",
  BgColorValue
> = { warning: "warning-primary", error: "error-primary" };

const isValidWeightErrorLevelToTextColor: Record<
  "warning" | "error",
  TextColorValue
> = { warning: "warning", error: "error" };

export const SplitVariantControls = memo(function SplitVariantControls({
  variants,
  onVariantNameChange,
  onVariantWeightChange,
  onAddVariant,
  onRemoveVariant,
  onBalanceWeights,
  readOnly = false,
  totalWeight,
  isWeightValid,
}: SplitVariantControlsProps) {
  const { t } = useTranslation();
  return (
    <Flex dir="column" gap="md" justify="flex-start">
      {variants.map((variant, index) => (
        <Flex
          align="center"
          dir="row"
          flex={1}
          gap="xs"
          justify="space-between"
          key={variant.id}
        >
          <div
            className={classNames({
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [css.variantColor1]: index === 0,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [css.variantColor2]: index === 1,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [css.variantColor3]: index === 2,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [css.variantColor4]: index === 3,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [css.variantColor5]: index === 4,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [css.variantColor6]: index === 5,
            })}
            style={{
              width: "4px",
              height: "28px",
              borderRadius: "var(--border-radius-xs)",
            }}
          />

          <RedoTextInput
            placeholder={`Variant ${String.fromCharCode(65 + index)}`}
            setValue={(value) => onVariantNameChange(variant.id || "", value)}
            state={readOnly ? "disabled" : "default"}
            value={variant.name || ""}
          />

          {/* Weight input or just remove button */}
          <Flex gap="xs">
            <RedoTextInput
              className={classNames(css.noSpinners)}
              inputTypeHint="number"
              max={100}
              min={0}
              setValue={(value) =>
                onVariantWeightChange(variant.id || "", value)
              }
              state={readOnly ? "disabled" : "default"}
              trailingActions={
                <Flex align="center" justify="center">
                  <PercentIcon style={{ width: "14px", height: "14px" }} />
                </Flex>
              }
              value={(variant.weight || 0).toString()}
            />
            <RedoButton
              aria-label={t("Remove")}
              disabled={readOnly || variants.length <= 2}
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={() => onRemoveVariant(variant.id || "")}
              size="sm"
            />
          </Flex>
        </Flex>
      ))}

      {/* Action buttons */}
      <Flex dir="row" gap="md" justify="flex-start" mt="md">
        <RedoButton
          disabled={readOnly || variants.length >= MAX_VARIANTS}
          hierarchy="secondary"
          IconLeading={PlusIcon}
          onClick={onAddVariant}
          text={t("Add variation")}
        />
        <RedoButton
          disabled={readOnly}
          hierarchy="tertiary"
          onClick={onBalanceWeights}
          text={t("Balance")}
        />
      </Flex>

      {/* Warning message if weights don't add up to 100% */}
      {!isWeightValid.isValid && (
        <Flex
          align="center"
          bgColor={
            isValidWeightErrorLevelToBgColor[
              isWeightValid.errorLevel ?? "error"
            ]
          }
          dir="row"
          gap="sm"
          mt="md"
          textColor={
            isValidWeightErrorLevelToTextColor[
              isWeightValid.errorLevel ?? "error"
            ]
          }
        >
          <Flex
            align="center"
            justify="center"
            style={{ width: "16px", height: "16px" }}
          >
            <AlertTriangleIcon />
          </Flex>
          <Text fontSize="xs">{isWeightValid.errorMessage}</Text>
        </Flex>
      )}

      {/* Weight visualization bar */}
      {totalWeight > 0 && variants.length > 0 && (
        <Flex dir="column" gap="xs" mt="lg">
          <Flex
            dir="row"
            gap="none"
            style={{
              height: "12px",
              overflow: "hidden",
              width: "100%",
              borderRadius: "var(--border-radius-sm)",
            }}
          >
            {variants.map((variant, index) => (
              <Flex
                className={classNames({
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  [css.variantColor1]: index === 0,
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  [css.variantColor2]: index === 1,
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  [css.variantColor3]: index === 2,
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  [css.variantColor4]: index === 3,
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  [css.variantColor5]: index === 4,
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  [css.variantColor6]: index === 5,
                })}
                key={variant.id}
                style={{ width: `${variant.weight}%`, height: "100%" }}
              />
            ))}
          </Flex>

          <Flex dir="row" justify="space-between">
            <Text fontSize="xs">0%</Text>
            <Text fontSize="xs">100%</Text>
            {!isWeightValid && (
              <Text fontSize="xs" textColor="warning">
                {totalWeight}%
              </Text>
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});
